// /settings/billing — уровень доступа, возврат из оплаты, квоты, способ оплаты.
//
// После переезда продажи в IEC ID экран отвечает на три разных вопроса, и их
// нельзя мешать:
//   1. ЧТО у человека сейчас и откуда (`CurrentPlanPanel` — право экосистемы
//      ∪ локальная подписка);
//   2. ДОЕХАЛА ли оплата, если он только что вернулся из их витрины
//      (`?iec=return` → `IecReturnPanel`, опрос прав, не «спасибо за покупку»);
//   3. СКОЛЬКО осталось по лимитам (квоты — наши, тарифом не переезжали).
//
// Способ оплаты показывается ТОЛЬКО при живой локальной подписке: у права от
// экосистемы карта живёт в их биллинге, и наша плашка про неё соврала бы.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, TactileDial } from "@studyhub/ui";
import { IconArrowRight, IconCreditCard, IconCrown, IconGauge } from "@tabler/icons-react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { isLocalSubscriptionAlive } from "@/components/billing/_entitlement";
import { IEC_RETURN_FLAG } from "@/components/billing/_use-iec-checkout";
import { CurrentPlanPanel } from "@/components/billing/current-plan-panel";
import { IecReturnPanel } from "@/components/billing/iec-return-panel";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { SettingsSection } from "@/components/settings/settings-shell";
import { CountUp } from "@/components/shared/count-up";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { type QuotaKind, useQuota } from "@/hooks/use-quota";
import { isCommerceVisible } from "@/lib/module-availability/commerce";
import { api } from "@/lib/trpc-client";
import { useSubscriptionModalStore } from "@/stores/subscription-modal";

/**
 * `?iec=return` дописывает СЕРВЕР в адрес возврата (`mutations/handoff.ts`).
 * Своих параметров витрина не добавляет и токена заказа не отдаёт — признак
 * возврата это всё, что у экрана есть.
 */
const searchSchema = z.object({
	iec: z.literal(IEC_RETURN_FLAG).optional(),
});

export const Route = createFileRoute("/settings/billing")({
	// Гейт коммерции: когда продажа скрыта, роут не должен существовать даже по
	// прямой ссылке. Проверка в `beforeLoad`, а не в компоненте, — чтобы страница
	// не мигнула тарифами перед редиректом.
	beforeLoad: () => {
		if (!isCommerceVisible()) throw redirect({ to: "/settings" });
	},
	validateSearch: tolerantSearch(searchSchema),
	component: BillingRoute,
	pendingComponent: PagePendingFallback,
});

/** Квоты-дайлы, показываемые на странице билинга. */
const QUOTA_DIALS: Array<{ kind: QuotaKind; labelKey: string }> = [
	{ kind: "notes", labelKey: "billing.quota.notes" },
	{ kind: "recordings", labelKey: "billing.quota.recordings" },
	{ kind: "audioRecaps", labelKey: "billing.quota.audioRecaps" },
];

function BillingRoute() {
	const { t } = useTranslation("settings");
	const { iec } = Route.useSearch();
	const navigate = useNavigate();
	const openManage = useSubscriptionModalStore((s) => s.open);

	const subQuery = api.billing.subscriptions.get.useQuery(undefined, { staleTime: 30_000 });
	const paymentMethod = subQuery.data?.paymentMethod ?? null;
	// Способ оплаты — только у ЖИВОЙ локальной подписки. Право от экосистемы
	// оплачивается в их биллинге, и карту им меняют там же.
	const localAlive = isLocalSubscriptionAlive(subQuery.data?.subscription ?? null);

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("billing.title")}
				description={t("billing.subtitle")}
				icon={<IconCrown stroke={1.75} />}
				category="streak"
			/>

			{iec === IEC_RETURN_FLAG && (
				<IecReturnPanel
					// Признак возврата снимаем из адреса — иначе перезагрузка
					// страницы через час снова запустила бы проверку оплаты.
					onDone={() => void navigate({ to: "/settings/billing", search: {}, replace: true })}
				/>
			)}

			<CurrentPlanPanel />

			<SettingsSection title={t("billing.quotaTitle")} icon={<IconGauge stroke={1.75} />}>
				<div className="stagger-enter grid grid-cols-1 gap-4 px-4 py-5 sm:grid-cols-3">
					{QUOTA_DIALS.map((q) => (
						<QuotaDial key={q.kind} kind={q.kind} label={t(q.labelKey)} />
					))}
				</div>
				<div className="px-4 py-3">
					<Button asChild variant="ghost" size="sm">
						<Link to="/pricing">
							{t("billing.compareLimits")}
							<IconArrowRight size={14} stroke={1.75} className="ml-1.5" />
						</Link>
					</Button>
				</div>
			</SettingsSection>

			{localAlive && (
				<SettingsSection title={t("billing.paymentTitle")} icon={<IconCreditCard stroke={1.75} />}>
					<div className="flex items-center gap-3 px-4 py-3.5">
						<div className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-tile)] bg-[var(--well)] text-muted-foreground shadow-e0">
							<IconCreditCard size={18} stroke={1.75} />
						</div>
						<div className="min-w-0 flex-1">
							<div className="text-sm font-medium text-foreground">
								{paymentMethod?.brand ?? t("billing.paymentDefault")}
							</div>
							<div className="text-xs tabular-nums text-muted-foreground">
								{paymentMethod?.last4
									? t("billing.paymentLast4", { last4: paymentMethod.last4 })
									: t("billing.paymentManagedByProvider")}
							</div>
						</div>
						<Button variant="outline" size="sm" onClick={() => openManage("payment-method")}>
							{t("billing.paymentUpdate")}
						</Button>
					</div>
				</SettingsSection>
			)}
		</SettingsPageContainer>
	);
}

/** QuotaDial — тактильный кольцевой индикатор использования квоты. */
function QuotaDial({ kind, label }: { kind: QuotaKind; label: string }) {
	const { t } = useTranslation("settings");
	const snapshot = useQuota(kind);
	// Безлимит = limit === null (Pro-tier / нет лимита для kind), НЕ limit === 0.
	// При null показываем символ ∞ + «безлимит», а не «Infinity / из » — у безлимита
	// snapshot.remaining === Infinity и snapshot.limit === null, и их нельзя рендерить как числа.
	const unlimited = snapshot.limit === null;
	// Дайл показывает «осталось» — заполненность = доступный остаток.
	const percent = unlimited ? 100 : snapshot.percentRemaining;

	return (
		<div className="flex flex-col items-center gap-2 text-center">
			<TactileDial
				value={percent}
				size={108}
				thickness={9}
				label={
					unlimited ? (
						<span aria-hidden className="text-2xl leading-none text-primary">
							∞
						</span>
					) : (
						<CountUp value={snapshot.remaining} className="tabular-nums" />
					)
				}
				sublabel={
					unlimited ? t("billing.quotaUnlimited") : t("billing.quotaOf", { limit: snapshot.limit })
				}
			/>
			<span className="text-xs font-medium text-foreground">{label}</span>
		</div>
	);
}
